/**
 * 布局组件 - UI/UX Pro Max 优化版
 *
 * 设计系统：
 * - 风格：Soft UI Evolution + Minimalism
 * - 颜色：Primary #3B82F6, CTA #F97316
 * - 字体：Poppins (标题) + Open Sans (正文)
 *
 * 优化点：
 * 1. 完整导航（首页 / 上传文本 / 生成大纲）
 * 2. 移动端响应式汉堡菜单
 * 3. 浮动导航栏 + Glassmorphism 效果
 * 4. 流畅的页面过渡动画
 */

import { ReactNode, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, FileText, Home, Menu, X, Settings, User, LogOut, Clock } from 'lucide-react';
import { useAuthStore } from '../store/auth';

interface LayoutProps {
  children: ReactNode;
}

// 完整导航项 - 简化为 2 个核心入口
const navItems = [
  { path: '/', label: '首页', icon: Home },
  { path: '/v3', label: '智能拆书', icon: Sparkles },
  { path: '/history', label: '历史记录', icon: Clock },
  { path: '/reference', label: '对标爆款', icon: FileText },
];

// 设置入口（右侧图标）
const settingsItem = { path: '/settings', label: '设置', icon: Settings };

export default function Layout({ children }: LayoutProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { user, isAuthenticated, logout } = useAuthStore();

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <div className="min-h-screen flex flex-col">
      {/* Header - 浮动设计 */}
      <header className="sticky top-0 z-50 px-4 py-4">
        <div className="mx-auto max-w-[1400px]">
          <div className="bg-white/80 backdrop-blur-lg rounded-2xl shadow-lg shadow-gray-200/50 border border-gray-100 px-6 py-3">
            <div className="flex items-center justify-between">
              {/* Logo */}
              <Link to="/" className="flex items-center gap-3 group">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary-500 to-accent-500 flex items-center justify-center shadow-lg shadow-primary-500/25 group-hover:shadow-primary-500/40 transition-shadow">
                  <Sparkles className="w-5 h-5 text-white" />
                </div>
                <span className="text-xl font-bold bg-gradient-to-r from-primary-600 to-accent-600 bg-clip-text text-transparent">
                  爆款大纲
                </span>
              </Link>

              {/* Desktop Nav */}
              <nav className="hidden md:flex items-center gap-1">
                {navItems.map((item) => {
                  const Icon = item.icon;
                  const isActive = location.pathname === item.path;

                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      className={`flex items-center gap-2 px-4 py-2 rounded-xl transition-all cursor-pointer ${
                        isActive
                          ? 'bg-primary-50 text-primary-600'
                          : 'text-gray-600 hover:bg-gray-50'
                      }`}
                    >
                      <Icon className="w-4 h-4" />
                      <span className="text-sm font-medium">{item.label}</span>
                    </Link>
                  );
                })}
                {/* 设置按钮 */}
                <Link
                  to={settingsItem.path}
                  className={`flex items-center gap-2 px-4 py-2 rounded-xl transition-all cursor-pointer ml-2 ${
                    location.pathname === settingsItem.path
                      ? 'bg-slate-100 text-slate-700'
                      : 'text-gray-500 hover:bg-gray-50'
                  }`}
                  title={settingsItem.label}
                >
                  <settingsItem.icon className="w-4 h-4" />
                </Link>

                {/* 用户状态 / 登录按钮 */}
                {isAuthenticated && user ? (
                  <div className="flex items-center gap-2 ml-2">
                    <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-indigo-50 text-indigo-700">
                      <User className="w-4 h-4" />
                      <span className="text-sm font-medium">{user.email}</span>
                      <span className="text-xs bg-indigo-200 px-2 py-0.5 rounded-full">
                        {user.credits}次
                      </span>
                    </div>
                    <button
                      onClick={handleLogout}
                      className="p-2 rounded-xl text-gray-500 hover:bg-gray-50 transition-colors cursor-pointer"
                      title="退出登录"
                    >
                      <LogOut className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <Link
                    to="/auth"
                    className="flex items-center gap-2 px-4 py-2 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white transition-colors cursor-pointer ml-2"
                  >
                    <User className="w-4 h-4" />
                    <span className="text-sm font-medium">登录</span>
                  </Link>
                )}
              </nav>

              {/* Mobile Menu Button */}
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="md:hidden p-2 rounded-xl hover:bg-gray-50 transition-colors cursor-pointer"
              >
                {mobileMenuOpen ? (
                  <X className="w-6 h-6 text-gray-600" />
                ) : (
                  <Menu className="w-6 h-6 text-gray-600" />
                )}
              </button>
            </div>

            {/* Mobile Menu */}
            <AnimatePresence>
              {mobileMenuOpen && (
                <motion.nav
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="md:hidden overflow-hidden"
                >
                  <div className="pt-4 pb-2 space-y-1">
                    {navItems.map((item) => {
                      const Icon = item.icon;
                      const isActive = location.pathname === item.path;

                      return (
                        <Link
                          key={item.path}
                          to={item.path}
                          onClick={() => setMobileMenuOpen(false)}
                          className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all cursor-pointer ${
                            isActive
                              ? 'bg-primary-50 text-primary-600'
                              : 'text-gray-600 hover:bg-gray-50'
                          }`}
                        >
                          <Icon className="w-5 h-5" />
                          <span className="font-medium">{item.label}</span>
                        </Link>
                      );
                    })}
                    {/* 设置入口 */}
                    <Link
                      to={settingsItem.path}
                      onClick={() => setMobileMenuOpen(false)}
                      className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all cursor-pointer border-t border-gray-100 mt-2 pt-3 ${
                        location.pathname === settingsItem.path
                          ? 'bg-slate-100 text-slate-700'
                          : 'text-gray-500 hover:bg-gray-50'
                      }`}
                    >
                      <settingsItem.icon className="w-5 h-5" />
                      <span className="font-medium">{settingsItem.label}</span>
                    </Link>
                  </div>
                </motion.nav>
              )}
            </AnimatePresence>
          </div>
        </div>
      </header>

      {/* Main */}
      <main className="flex-1">
        <motion.div
          key={location.pathname}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          transition={{ duration: 0.2 }}
        >
          {children}
        </motion.div>
      </main>

      {/* Footer - 简化 */}
      <footer className="py-8 px-4">
        <div className="mx-auto max-w-[1400px] text-center">
          <p className="text-gray-400 text-sm">
            爆款大纲生成器 · 让每个故事都有爆款的潜质
          </p>
          <p className="text-gray-300 text-xs mt-2">
            Powered by AI
          </p>
        </div>
      </footer>
    </div>
  );
}
